/**
 * CR309: Details CTA Block
 * CR310: Updated to render "Details ›" pill bottom-right on the front face
 * 
 * Renders the "Details" trigger button with configurable placement:
 * - "floating": Absolute positioned in bottom-right of the card
 * - "inline": Rendered inside the card's footer/actions row
 * - "responsive": Floating on mobile, inline on desktop (breakpoint-driven)
 * 
 * CR310 Enhancement: Now renders as a small pill with "Details ›" label
 * by default, positioned bottom-right of the card.
 */

import React from "react";
import type { DealCardBlockProps, ResolvedDealCardSpecV2 } from "@/lib/deal-cards/specV2/types";
import type { Deal } from "@/lib/types/deals";
import { cn } from "@/lib/utils";

// ================================================================
// CONFIG TYPES
// ================================================================

export type DetailsCTAPlacement = "floating" | "inline" | "responsive";
export type DetailsCTABreakpoint = "sm" | "md" | "lg";

export interface DetailsCTAConfig {
  /** Whether the CTA is enabled */
  enabled?: boolean;
  /** Button label text - CR310: default "Details ›" */
  label?: string;
  /** Placement mode */
  placement?: DetailsCTAPlacement;
  /** Breakpoint for responsive mode (when to switch from floating to inline) */
  breakpoint?: DetailsCTABreakpoint;
  /** Floating offset from edges */
  floatingOffset?: {
    right?: number;
    bottom?: number;
  };
  /** Show icon alongside label */
  showIcon?: boolean;
  /** Button variant - CR310: default "ghost" for subtle pill */
  variant?: "primary" | "secondary" | "ghost";
}

export const DEFAULT_DETAILS_CTA_CONFIG: DetailsCTAConfig = {
  enabled: true,
  label: "Details ›", // CR310: Updated default label
  placement: "floating", // CR310: Floating by default for front face
  breakpoint: "md",
  floatingOffset: { right: 12, bottom: 12 },
  showIcon: false,
  variant: "ghost", // CR310: Ghost for subtle pill appearance
};

// ================================================================
// HELPERS
// ================================================================

/**
 * CR310: Check if the details (back) surface has at least one enabled block
 * or if details_content is enabled. Used as a guardrail to hide Details CTA
 * when there's nothing to show on the back.
 */
export function hasEnabledDetailsContent(spec: ResolvedDealCardSpecV2 | undefined): boolean {
  if (!spec) return true; // Default to showing if no spec (fallback)
  
  // Check if any details block is enabled
  const detailsBlocks = spec.surfaces.details.blocks;
  const hasEnabledBlock = detailsBlocks.some(block => 
    block.enabled && block.id !== "back_header" && block.id !== "back_nav"
  );
  
  return hasEnabledBlock;
}

// ================================================================
// BLOCK COMPONENT
// ================================================================

interface DetailsCTABlockProps extends DealCardBlockProps<Deal> {
  /** Click handler to trigger details view */
  onClick?: (e: React.MouseEvent) => void;
  /** Force a specific render mode (for flip presentation integration) */
  renderMode?: "inline-only" | "floating-only" | "auto";
  /** Spec reference for checking back surface content - CR310 */
  spec?: ResolvedDealCardSpecV2;
}

/**
 * Details CTA Block - the trigger button that opens the deal details view.
 * 
 * CR310: Renders as a small pill "Details ›" in bottom-right of the card.
 * 
 * Visibility guardrail: Only shows if the back surface has enabled content.
 */
export const DetailsCTABlock: React.FC<DetailsCTABlockProps> = ({
  deal,
  block,
  context,
  onClick,
  renderMode = "auto",
  spec,
}) => {
  // Block must be enabled
  if (!block.enabled) return null;
  
  // Parse config with defaults
  const config: DetailsCTAConfig = {
    ...DEFAULT_DETAILS_CTA_CONFIG,
    ...(block.config as DetailsCTAConfig || {}),
  };
  
  // If explicitly disabled via config, return null
  if (config.enabled === false) return null;
  
  // CR310: Visibility guardrail - don't show if back has no content
  if (spec && !hasEnabledDetailsContent(spec)) {
    return null;
  }
  
  const label = config.label || "Details ›";
  const placement = config.placement || "floating";
  const breakpoint = config.breakpoint || "md";
  const offset = config.floatingOffset || { right: 12, bottom: 12 };
  const variant = config.variant || "ghost";
  
  // Determine what to render based on placement and renderMode
  const shouldRenderFloating = 
    renderMode === "floating-only" || 
    (renderMode === "auto" && (placement === "floating" || placement === "responsive"));
  
  const shouldRenderInline = 
    renderMode === "inline-only" || 
    (renderMode === "auto" && (placement === "inline" || placement === "responsive"));
  
  // Breakpoint classes for responsive mode
  const breakpointHide: Record<DetailsCTABreakpoint, string> = {
    sm: "sm:hidden",
    md: "md:hidden",
    lg: "lg:hidden",
  };
  
  const breakpointShow: Record<DetailsCTABreakpoint, string> = {
    sm: "hidden sm:flex",
    md: "hidden md:flex",
    lg: "hidden lg:flex",
  };
  
  // CR310: Updated button styles for pill appearance
  const getButtonStyles = (isFloating: boolean) => cn(
    // CR310: Pill styling - compact, rounded-full, subtle
    "px-2.5 py-1 text-xs font-medium rounded-full transition-all duration-200",
    "focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-primary/30",
    // Variant styles
    variant === "primary" && "bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm",
    variant === "secondary" && "bg-secondary text-secondary-foreground hover:bg-secondary/80",
    // CR310: Ghost variant for subtle pill - default for front face
    variant === "ghost" && cn(
      "bg-background/90 text-foreground/80 hover:text-foreground",
      "border border-border/50 hover:border-border",
      "backdrop-blur-sm shadow-sm hover:shadow",
    ),
    // Positioning
    isFloating && "absolute z-20",
  );
  
  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onClick?.(e);
  };
  
  // Floating style (absolute positioned on the card)
  const floatingStyle: React.CSSProperties = {
    right: offset.right,
    bottom: offset.bottom,
  };
  
  return (
    <>
      {/* Floating version - absolute positioned on card */}
      {shouldRenderFloating && (
        <button
          onClick={handleClick}
          className={cn(
            getButtonStyles(true),
            // For responsive mode: hide at breakpoint and above
            placement === "responsive" && breakpointHide[breakpoint],
          )}
          style={floatingStyle}
          aria-label="Show deal details"
          data-block="details_cta"
          data-block-id={block.id}
          data-placement="floating"
        >
          {label}
        </button>
      )}
      
      {/* Inline version - for footer/actions row */}
      {shouldRenderInline && (
        <button
          onClick={handleClick}
          className={cn(
            getButtonStyles(false),
            "flex items-center justify-center",
            // For responsive mode: show only at breakpoint and above
            placement === "responsive" && breakpointShow[breakpoint],
            // For pure inline mode: always visible
            placement === "inline" && "flex",
          )}
          aria-label="Show deal details"
          data-block="details_cta"
          data-block-id={block.id}
          data-placement="inline"
        >
          {label}
        </button>
      )}
    </>
  );
};

export default DetailsCTABlock;

// ================================================================
// CONFIG HELPERS
// ================================================================

/**
 * Get the default Details CTA block spec
 */
export function getDefaultDetailsCTABlockSpec() {
  return {
    id: "details_cta",
    type: "details_cta" as const,
    enabled: true,
    order: 12, // After cta_button
    config: { ...DEFAULT_DETAILS_CTA_CONFIG },
  };
}

/**
 * Merge user config with defaults
 */
export function mergeDetailsCTAConfig(config?: Partial<DetailsCTAConfig>): DetailsCTAConfig {
  return {
    ...DEFAULT_DETAILS_CTA_CONFIG,
    ...config,
    floatingOffset: {
      ...DEFAULT_DETAILS_CTA_CONFIG.floatingOffset,
      ...config?.floatingOffset,
    },
  };
}
